/**
 * G014 — 存档信封（Envelope）。
 *
 * 本模块**纯逻辑、不碰任何存储**：构造信封、校验、迁移。IndexedDB 属于宿主环境，
 * 由 apps/game 的适配器负责（`.claude/rules/simulation.md` 禁止 simulation-core
 * 导入 IndexedDB）。这样存档的正确性可以完全无头测试。
 *
 * 契约来自 docs/03-data-contracts/save-contract.md：
 * - 顶层必须带产品名 `CRATER ZERO` 与命名空间 `crater_zero` —— 拿错游戏的存档要拒
 * - `saveVersion` 管结构迁移，`contentVersion` 管内容包兼容
 * - 派生缓存不入档，读取后重建
 * - UI 只存摄像机与面板偏好，不存 hover/drag 这类瞬时状态
 */

import type { WorldState } from '../world-state.js';
import { fnv1a32, canonicalize } from '../state-hash.js';

/** 名称锁：这两个值不得随意更改，`check:name-lock` 会扫描。 */
export const SAVE_PRODUCT = 'CRATER ZERO';
export const SAVE_NAMESPACE = 'crater_zero';

/** 当前存档结构版本。任何对 WorldState 形状的破坏性改动都必须 +1 并加迁移。 */
export const SAVE_VERSION = 1;

/** 摄像机等纯展示状态：存进档但**不进权威状态**，读取后也不喂给内核。 */
export interface ViewMeta {
  readonly focusX: number;
  readonly focusZ: number;
  readonly yaw: number;
  readonly pitch: number;
  readonly dist: number;
}

export interface SaveEnvelope {
  readonly product: string;
  readonly namespace: string;
  readonly saveVersion: number;
  readonly contentVersion: string;
  /** 存档创建时的游戏内时间，用于存档列表展示（不参与校验）。 */
  readonly label: { day: number; minuteOfDay: number; buildings: number };
  /** 真实世界时间戳（仅展示；模拟结果绝不依赖它）。 */
  readonly createdAtMs: number;
  readonly view: ViewMeta | null;
  readonly world: WorldState;
  /** 对 world + 版本字段的校验和，防止半截写入或手改。 */
  readonly checksum: string;
}

export type SaveRejection =
  | 'SAVE_NOT_OBJECT'
  | 'SAVE_WRONG_PRODUCT'
  | 'SAVE_WRONG_NAMESPACE'
  | 'SAVE_VERSION_UNSUPPORTED'
  | 'SAVE_CHECKSUM_MISMATCH'
  | 'SAVE_WORLD_MALFORMED';

export interface LoadResult {
  ok: boolean;
  rejection?: SaveRejection;
  envelope?: SaveEnvelope;
  /** 迁移过程说明，供 UI 与报告展示。 */
  migrations?: string[];
}

/**
 * 校验和覆盖 world 与三个版本字段。
 *
 * **不覆盖** `createdAtMs` 与 `label` —— 它们是展示信息，改动不影响存档有效性；
 * 把它们算进去只会让「重命名存档」这类无害操作变成损坏档。
 */
export function checksumOf(
  world: WorldState, saveVersion: number, contentVersion: string,
): string {
  return fnv1a32(canonicalize({ world, saveVersion, contentVersion }));
}

export interface BuildEnvelopeOptions {
  readonly world: WorldState;
  readonly contentVersion: string;
  /** 真实时间戳由调用方注入 —— 模拟层内部禁止 Date.now()。 */
  readonly createdAtMs: number;
  readonly view?: ViewMeta | null;
  readonly buildings?: number;
}

export function buildEnvelope(opts: BuildEnvelopeOptions): SaveEnvelope {
  const { world, contentVersion, createdAtMs } = opts;
  return {
    product: SAVE_PRODUCT,
    namespace: SAVE_NAMESPACE,
    saveVersion: SAVE_VERSION,
    contentVersion,
    label: {
      day: world.clock.day,
      minuteOfDay: world.clock.minuteOfDay,
      buildings: opts.buildings ?? 0,
    },
    createdAtMs,
    view: opts.view ?? null,
    world,
    checksum: checksumOf(world, SAVE_VERSION, contentVersion),
  };
}

/* ===================== 迁移框架 ===================== */

export interface Migration {
  /** 从这个版本迁移到 from+1。 */
  readonly from: number;
  readonly describe: string;
  apply(raw: Record<string, unknown>): Record<string, unknown>;
}

/**
 * 迁移表。目前为空 —— saveVersion 还是 1，没有历史版本需要升级。
 *
 * 留着框架而不是等需要时再加，是因为**第一次真正需要迁移时，往往已经有玩家存档
 * 在外面了**；那时再设计迁移机制，就得同时处理「没有迁移机制的旧档」。
 */
export const MIGRATIONS: readonly Migration[] = [];

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** WorldState 的最小结构校验：只查加载后立刻会用到的骨架字段。 */
function looksLikeWorld(v: unknown): boolean {
  if (!isPlainObject(v)) return false;
  const clock = v['clock'], entities = v['entities'], spatial = v['spatial'];
  if (!isPlainObject(clock) || typeof clock['tick'] !== 'number') return false;
  if (!isPlainObject(entities) || !Array.isArray(entities['order'])) return false;
  if (!isPlainObject(spatial) || !isPlainObject(spatial['cells'])) return false;
  return true;
}

/**
 * 解析并校验一份存档。
 *
 * 顺序是有意的：**先迁移，再完整校验**（存档契约明写）。因为旧版本的存档在
 * 迁移之前本来就不满足当前结构，先校验会把所有旧档判成损坏。
 */
export function parseEnvelope(raw: unknown, contentVersion?: string): LoadResult {
  if (!isPlainObject(raw)) return { ok: false, rejection: 'SAVE_NOT_OBJECT' };
  if (raw['product'] !== SAVE_PRODUCT) return { ok: false, rejection: 'SAVE_WRONG_PRODUCT' };
  if (raw['namespace'] !== SAVE_NAMESPACE) return { ok: false, rejection: 'SAVE_WRONG_NAMESPACE' };

  const v = raw['saveVersion'];
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > SAVE_VERSION) {
    return { ok: false, rejection: 'SAVE_VERSION_UNSUPPORTED' };
  }

  // 1) 迁移到当前版本
  let cur: Record<string, unknown> = { ...raw };
  const migrations: string[] = [];
  let ver = v;
  while (ver < SAVE_VERSION) {
    const m = MIGRATIONS.find((x) => x.from === ver);
    if (!m) return { ok: false, rejection: 'SAVE_VERSION_UNSUPPORTED' };
    cur = m.apply(cur);
    migrations.push(m.describe);
    ver += 1;
  }
  cur['saveVersion'] = SAVE_VERSION;

  // 2) 结构校验
  if (!looksLikeWorld(cur['world'])) return { ok: false, rejection: 'SAVE_WORLD_MALFORMED' };

  // 3) 校验和。迁移过的档校验和必然对不上原值，按迁移后的内容重算
  const world = cur['world'] as WorldState;
  const cv = typeof cur['contentVersion'] === 'string' ? cur['contentVersion'] : '';
  const expect = checksumOf(world, SAVE_VERSION, cv);
  if (migrations.length === 0 && cur['checksum'] !== expect) {
    return { ok: false, rejection: 'SAVE_CHECKSUM_MISMATCH' };
  }

  const envelope: SaveEnvelope = {
    product: SAVE_PRODUCT,
    namespace: SAVE_NAMESPACE,
    saveVersion: SAVE_VERSION,
    contentVersion: cv,
    label: isPlainObject(cur['label'])
      ? cur['label'] as SaveEnvelope['label']
      : { day: world.clock.day, minuteOfDay: world.clock.minuteOfDay, buildings: 0 },
    createdAtMs: typeof cur['createdAtMs'] === 'number' ? cur['createdAtMs'] : 0,
    view: isPlainObject(cur['view']) ? cur['view'] as unknown as ViewMeta : null,
    world,
    checksum: expect,
  };

  // 内容包版本不一致不算错误：允许读档，由 UI 提示可能的行为差异
  void contentVersion;
  return { ok: true, envelope, migrations };
}

/** 导出为可读 JSON（玩家备份用）。 */
export function exportEnvelopeJson(env: SaveEnvelope): string {
  return JSON.stringify(env, null, 2);
}
